import React from 'react';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import ReactSelectMultiSelect from '../Inputs/ReactSelectMultiSelect';

// Keep styling the same as used in AllTableData.js
const colourStyles = {
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--black)',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 4,
    marginTop: 0,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({
    ...base,
    padding: 0,
  }),
  control: (base, state) => {
    const hasError = !!(state && state.selectProps && state.selectProps.error);
    return {
      ...base,
      // background: 'var(--primary-50)',
      borderRadius: 4,
      // ensure an actual border is applied (react-select base may not use borderColor alone)
      border: `1px solid ${hasError ? 'var(--functional-red)' : 'var(--primary-500)'}`,
      boxShadow: state.isFocused ? null : null,
      '&:hover': {
        border: `1px solid ${hasError ? 'var(--functional-red)' : 'var(--primary-500)'}`,
      },
      minHeight: 54,
      height: 54,
    };
  },
  option: (provided, state) => {
    const isAddNew = state.data?.value === 'add-new';
    return {
      ...provided,
      cursor: 'pointer',
      backgroundColor: state.isSelected ? 'var(--primary-500)' : 'white',
      border: isAddNew ? '2px solid var(--primary-500)' : 'none',
      '&:hover': {
        backgroundColor: state.isSelected ? 'var(--primary-300)' : 'var(--background)',
      },
      height: 54, // Match input height
      display: 'flex',
      alignItems: 'center',
    };
  },
  // Styles for multi-value pills and the remove icon
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--primary-50)',
    borderRadius: 4,
    paddingLeft: 12,
    paddingRight: 8,
    margin: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--body)',
    paddingRight: 6,
    fontSize: 16,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--body)',
    borderRadius: 8,
    fontSize: 24,
    ':hover': {
      backgroundColor: 'transparent',
      color: '#00000099',
    },
  }),
};

// Custom ValueContainer that dynamically calculates visible chips and appends a count chip
const ValueContainer = ({ children, ...props }) => {
  let [values, input] = children;

  if (Array.isArray(values)) {
    const container = props.getValue().length ? props : null;
    const total = values.length;

    // Determine available width by reading the parent control width and subtracting paddings
    const controlEl = props.innerProps && props.innerProps.id ? document.getElementById(props.innerProps.id) : null;
    let availablePx = 0;
    if (controlEl) {
      const controlRect = controlEl.getBoundingClientRect();
      availablePx = controlRect.width - 24; // padding allowance
    }

    // Fallback if we can't measure
    if (!availablePx) {
      availablePx = 250;
    }

    // Estimate chip width; use a conservative average to avoid overflow
    const avgChipPx = 100; // average width of one chip including margins
    const maxVisible = Math.max(1, Math.floor((availablePx - 40) / avgChipPx));

    if (total > maxVisible) {
      const visible = values.slice(0, maxVisible);
      const remaining = total - maxVisible;

      // Count chip styled similarly to multiValue
      const CountChip = (
        <div className="prov__multi-value" style={{ backgroundColor: 'var(--primary-50)', borderRadius: 4, padding: '6px 8px', margin: 6 }}>
          <div className="prov__multi-value__label" style={{ color: 'var(--body)' }}>+{remaining}</div>
        </div>
      );

      values = (
        <>
          {visible}
          {CountChip}
        </>
      );
    }
  }

  return (
    <components.ValueContainer {...props}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

// Custom dropdown indicator showing an up arrow when open and down arrow when closed
const DropdownIndicator = (props) => {
  const { menuIsOpen } = props.selectProps || {};
  const stroke = 'currentColor';
  return (
    <components.DropdownIndicator {...props}>
      {menuIsOpen ? (
        <svg width="24" height="24" viewBox="0 0 16 16" aria-hidden="true">
          <polyline points="4,10 8,6 12,10" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 16 16" aria-hidden="true">
          <polyline points="4,6 8,10 12,6" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </components.DropdownIndicator>
  );
};

// Utility function to capitalize a string
function capitalize(str) {
  if (typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const SingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center">
        {data.icon && <span className="mr-2">{data.icon}</span>}
        {capitalize(data.label)}
      </div>
    </components.SingleValue>
  );
};

const Option = (props) => {
  const { data, isSelected } = props;
  const isAddNew = data.value === 'add-new';
  const textColorClass = isAddNew 
    ? (isSelected ? 'text-white font-semibold' : 'text-primary-500 font-semibold')
    : '';
  const iconColorClass = isAddNew && isSelected ? 'text-white' : 'text-primary-500';
  const iconElement = data.icon && isAddNew 
    ? React.cloneElement(data.icon, { className: iconColorClass })
    : data.icon;
  return (
    <components.Option {...props}>
      <div className={`flex items-center ${textColorClass}`}>
        {iconElement && <span className="mr-2">{iconElement}</span>}
        {data.label}
      </div>
    </components.Option>
  );
};

const Dropdown = ({
  name,
  options = [],
  isClearable = false,
  isMulti = false,
  isDisabled = false,
  maxVisibleValues = 6,
  placeholder = '',
  onChange,
  value,
  defaultValue,
  className = 'indiv-dropdown',
  eleRef,
  error
}) => {
  // Normalize options if strings are provided
  const normalizedOptions = Array.isArray(options)
    ? options.map((opt) =>
        typeof opt === 'string' ? { value: opt, label: opt } : opt
      )
    : [];

  return (
    <div className="dropdown">
      <div className="init-dropdown">
        <ReactSelectMultiSelect
          name={name}
          eleRef={eleRef}
          className={className}
          options={normalizedOptions}
          isClearableCheck={isClearable ? true : null}
          isMulti={isMulti ? true : null}
          isDisabled={isDisabled}
          hideSelectedOptions={false}
          ValueContainer={ValueContainer}
          styles={colourStyles}
          components={{ DropdownIndicator, Option, SingleValue }}
          classNamePrefix="prov"
          maxVisibleValues={maxVisibleValues}
          placeholder={placeholder}
          onChange={onChange}
          value={value}
          defaultValue={defaultValue}
          error={error}
        />
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  name: PropTypes.string,
  options: PropTypes.array,
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  className: PropTypes.string,
  eleRef: PropTypes.func,
};

export default Dropdown;


